import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'

import * as SvgIcons from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import { addToast } from 'components/Toast/Toast'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import SharedMediaModal from 'components/UI/Modal/SharedMediaModal/SharedMediaModal'
import { useModalContext } from 'context/modalContext'
import { AllIcons } from 'helpers/allIcons'
import { debounce } from 'helpers/hooks'
import { Icons } from 'helpers/icons'
import { getSupportedMediaFormats } from 'helpers/media'
import { getMediaAutocomplete } from 'services/endpoints/media'
import { useUserProfile } from 'store'
import { type IProgressInfo } from 'types/interfaces/ITypes'

export interface ICreatorSearchEntry {
    id: string | number
    display: string
    name: string
    avatarUrl: string
    username?: string
}

export interface ITaggedCreator extends ICreatorSearchEntry {
    public_tag: boolean
}

interface PostMediaInputsProps {
    filesAdded: boolean
    handleSetUpload: (e: React.ChangeEvent<HTMLInputElement>) => any
    toggleReorder?: () => void
    handleAddPollOpen: () => void
    handleAddGoalOpen: () => void
    reorderActive?: boolean
    confirmReorder?: any
    postType: string
    handleOpenRecorder: (type: string) => void
    hasAudio: boolean
    selectedFiles: File[]
    setSelectedFiles: any
    setProgressInfos: any
    progressInfos: { val: IProgressInfo[] }
    hasOnlyPhotos: boolean
    hasBlockingMediaForPollGoal: boolean
    selectedTaggedCreators: ITaggedCreator[]
    setSelectedTaggedCreators: React.Dispatch<React.SetStateAction<ITaggedCreator[]>>
    tagCreatorsOpen: boolean
    setTagCreatorsOpen: React.Dispatch<React.SetStateAction<boolean>>
    appendAttachments: (items: any[]) => Promise<void>
}

export const PostMediaInputs = ({
    filesAdded,
    handleSetUpload,
    appendAttachments,
    handleAddPollOpen,
    handleAddGoalOpen,
    postType,
    handleOpenRecorder,
    hasAudio,
    selectedFiles,
    setSelectedFiles,
    setProgressInfos,
    progressInfos,
    hasOnlyPhotos,
    hasBlockingMediaForPollGoal,
    selectedTaggedCreators,
    setSelectedTaggedCreators,
    tagCreatorsOpen,
    setTagCreatorsOpen
}: PostMediaInputsProps) => {
    const postImgRef = useRef<HTMLInputElement>(null)
    const tagSearchRef = useRef<HTMLInputElement>(null)
    const tagCreatorsRef = useRef<HTMLDivElement>(null)

    const { t } = useTranslation()
    const userData = useUserProfile()
    const modalData = useModalContext()
    const navigate = useNavigate()

    const acceptedMediaFormats = getSupportedMediaFormats()

    const [activeChatIcon, setActiveChatIcon] = useState(false)
    const [creatorSearchTerm, setCreatorSearchTerm] = useState('')
    const [creatorResults, setCreatorResults] = useState<ICreatorSearchEntry[]>([])
    const [creatorSearchLoading, setCreatorSearchLoading] = useState(false)
    const [pendingShowOnPostCreator, setPendingShowOnPostCreator] =
        useState<ICreatorSearchEntry | null>(null)

    const isRestricted = userData?.status === 'restricted'

    const isMissingPayoutMethod =
        userData?.role === 'model' &&
        !userData?.payout_method

    const payoutDisabled =
        isRestricted || isMissingPayoutMethod

    const payoutModalOpenedRef = useRef(false)

    const isStructuredPost = postType === 'poll' || postType === 'goal'
    const canOpenPollOrGoal = !isStructuredPost && !hasBlockingMediaForPollGoal
    const canKeepUsingMediaButtons = !isStructuredPost || hasOnlyPhotos

const normalizeSharedMediaItem = (item: any): any[] => {
    const media: any[] = []

    const pushPhoto = (photo: any, source: string) => {
        if (!photo?.url) return

        media.push({
            ...photo,
            percentage: 100,
            previewUrl: photo.url,
            storageUrl: photo.url,
            fileName: `shared-${source}-photo-${photo.id}`,
            type: 'photo',
            sharedSource: source
        })
    }

    const pushVideo = (
    video: any,
    source: string
    ) => {
    if (
        !video?.url &&
        !video?.src &&
        !video?.video_src
    ) {
        return
    }

    const videoUrl =
        video.url ??
        video.src ??
        video.video_src

    const customThumbnailUrl =
        typeof video.custom_thumbnail === 'string' &&
        video.custom_thumbnail.trim() !== ''
        ? video.custom_thumbnail
        : typeof video.customThumbnailUrl === 'string' &&
            video.customThumbnailUrl.trim() !== ''
            ? video.customThumbnailUrl
            : null

    const customThumbnailTimeValue =
        video.custom_thumbnail_time ??
        video.customThumbnailTime ??
        (
        typeof video.custom_thumbnail === 'number'
            ? video.custom_thumbnail
            : null
        )

    const customThumbnailTime =
        customThumbnailTimeValue != null &&
        Number.isFinite(
        Number(customThumbnailTimeValue)
        )
        ? Number(customThumbnailTimeValue)
        : null

    const hasCustomThumbnail =
        customThumbnailUrl !== null &&
        customThumbnailTime !== null

    media.push({
        ...video,

        id: video.id,
        video_id: video.video_id ?? video.id,

        percentage: 100,

        previewUrl: videoUrl,
        storageUrl:
        video.storage_path ??
        video.storageUrl ??
        videoUrl,

        imagePreview:
        hasCustomThumbnail
            ? customThumbnailUrl
            : video.active_thumb ??
            video.imagePreview ??
            null,

        customThumbnailUrl:
        hasCustomThumbnail
            ? customThumbnailUrl
            : null,

        /*
        * Internally this field is the custom frame timestamp
        * submitted back to the backend.
        */
        custom_thumbnail:
        hasCustomThumbnail
            ? customThumbnailTime
            : null,

        activeThumbnailIndex:
        hasCustomThumbnail
            ? null
            : (
                typeof video.activeThumbnailIndex === 'number'
                ? video.activeThumbnailIndex
                : null
            ),

        fileName:
        `shared-${source}-video-${video.id}`,

        type: 'video',
        processing: false,
        error: '',
        previewOn: video.preview ?? false,
        sharedSource: source
    })
    }

    if (item?.message?.message) {
        item.message.message.photos?.forEach((photo: any) => {
            pushPhoto(photo, 'message')
        })

        item.message.message.videos?.forEach((video: any) => {

            pushVideo(video, 'message')
        })
    }

    if (item?.post) {
        item.post.photos?.forEach((photo: any) => {
            pushPhoto(photo, 'post')
        })

        item.post.videos?.forEach((video: any) => {
            pushVideo(video, 'post')
        })

        const poll = item.post.poll
        if (poll?.background_src) {
            media.push({
                id: `poll-${poll.id}`,
                percentage: 100,
                previewUrl: poll.background_src_thumb || poll.background_src,
                storageUrl: poll.background_src,
                thumb_url: poll.background_src_thumb || poll.background_src,
                url: poll.background_src,
                fileName: `shared-post-poll-photo-${poll.id}`,
                type: 'photo',
                sharedSource: 'post_poll',
                post_id: item.post.id,
                poll_id: poll.id,
                poll,
                orientation: null
            })
        }

        const goal = item.post.goal
        if (goal?.background_src) {
            media.push({
                id: `goal-${goal.id}`,
                percentage: 100,
                previewUrl: goal.background_src,
                storageUrl: goal.background_src,
                thumb_url: goal.background_src,
                url: goal.background_src,
                fileName: `shared-post-goal-photo-${goal.id}`,
                type: 'photo',
                sharedSource: 'post_goal',
                post_id: item.post.id,
                goal_id: goal.id,
                goal,
                orientation: null
            })
        }
    }

    if (item?.story) {
        const story = item.story

        if (story.image_src) {
            media.push({
                ...story,
                id: story.id,
                percentage: 100,
                previewUrl: story.image_src,
                storageUrl: story.image_src,
                thumb_url: story.image_src,
                fileName: `shared-story-photo-${story.id}`,
                type: 'photo',
                sharedSource: 'story',
                story_id: story.id,
                post_id: story.post_id,
                orientation: story.image_orientation,
                text: story.text,
                duration: story.duration
            })
        }

        if (story.video_src) {
            media.push({
                ...story,
                id: story.id,
                percentage: 100,
                previewUrl: story.video_thumb_src ?? story.video_src,
                storageUrl: story.video_src,
                fileName: `shared-story-video-${story.id}`,
                type: 'video',
                sharedSource: 'story',
                story_id: story.id,
                post_id: story.post_id,
                orientation: story.video_orientation,
                thumbnails: story.video_thumb_src ? [story.video_thumb_src] : [],
                text: story.text,
                duration: story.duration
            })
        }
    }

    return media
}



    useEffect(() => {
    if (!modalData.isOpen) {
        setActiveChatIcon(false)
    }
    }, [modalData.isOpen])

    useEffect(() => {
        if (!userData) return

        if (!payoutDisabled) {
            payoutModalOpenedRef.current = false
            return
        }

        if (payoutModalOpenedRef.current) return

        payoutModalOpenedRef.current = true

        if (isRestricted) {
            modalData.addModal(
                'Restricted Account',
                <ConfirmModal
                    body="Please contact support for any assistance."
                    cancelText="Cancel"
                    confirmText="Submit Ticket"
                    cancelButtonWidth="13"
                    confirmButtonWidth="16"
                    cancelFn={() => {
                        modalData.clearModal()
                    }}
                    confirmFn={() => {
                        modalData.clearModal()
                        navigate('/support/home')
                    }}
                />
            )

            return
        }

        if (isMissingPayoutMethod) {
            modalData.addModal(
                t('payoutRequired'),
                <ConfirmModal
                    confirmButtonWidth="14"
                    body={t('payoutRequiredDescription')}
                    confirmText={t('gotoSettings')}
                    confirmFn={() => {
                        modalData.clearModal()
                        navigate('/settings/general/payout-settings')
                    }}
                />
            )
        }
    }, [
        userData,
        payoutDisabled,
        isRestricted,
        isMissingPayoutMethod,
        modalData,
        t,
        navigate
    ])

    useEffect(() => {
        if (tagCreatorsOpen) {
            setTimeout(() => tagSearchRef.current?.focus(), 50)
        }
    }, [tagCreatorsOpen])

    useEffect(() => {
        if (!tagCreatorsOpen) return

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node

            if (
                tagCreatorsRef.current &&
                !tagCreatorsRef.current.contains(target)
            ) {
                setTagCreatorsOpen(false)
                setPendingShowOnPostCreator(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('touchstart', handleClickOutside)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('touchstart', handleClickOutside)
        }
    }, [tagCreatorsOpen, setTagCreatorsOpen])

    const getCreators = async (term: string) => {
        const cleanTerm = term.trim()

        if (!cleanTerm) {
            setCreatorResults([])
            setCreatorSearchLoading(false)
            return
        }

        try {
            const response = await getMediaAutocomplete({
                searchTerm: cleanTerm
            })

            const transformedResponse = response.data.users.map((creator: any) => ({
                id: creator.id,
                display: creator.username,
                username: creator.username,
                name: creator.name,
                avatarUrl: creator.avatarSrc
            }))

            setCreatorResults(transformedResponse)
        } catch (error) {
            console.error('Failed to autocomplete creators', error)
            setCreatorResults([])
        } finally {
            setCreatorSearchLoading(false)
        }
    }

    const debouncedCreatorSearch = useCallback(
        debounce(async (term: string) => {
            await getCreators(term)
        }, 500),
        []
    )

    const removeTaggedCreator = (creatorId: string | number) => {
        setSelectedTaggedCreators(prev =>
            prev.filter(creator => creator.id !== creatorId)
        )
    }

    const addTaggedCreator = (
        creator: ICreatorSearchEntry,
        publicTag: boolean
    ) => {
        setSelectedTaggedCreators(prev => {
            const exists = prev.some(item => item.id === creator.id)

            if (exists) {
                return prev.map(item =>
                    item.id === creator.id
                        ? { ...item, public_tag: publicTag }
                        : item
                )
            }

            return [
                ...prev,
                {
                    ...creator,
                    public_tag: publicTag
                }
            ]
        })

        setPendingShowOnPostCreator(null)
        setCreatorSearchTerm('')
        setCreatorResults([])
        setCreatorSearchLoading(false)

        setTimeout(() => tagSearchRef.current?.focus(), 50)
    }

    return (
        <div
            className={`newpost__body__input newpost__body__input--media ${
                filesAdded ? 'newpost__body__input--media--active' : ''
            }`}
        >
            <div className="newpost__input__buttons">
                <IconButton
                    disabled={payoutDisabled || isStructuredPost}
                    icon={
                        <SvgIcons.IconImageOutline
                            color={
                                postType
                                    ? postType === 'media'
                                        ? '#200E32'
                                        : '#AFAFAF'
                                    : '#200E32'
                            }
                        />
                    }
                    clickFn={() => {
                        if (
                            userData?.status === 'restricted' &&
                            userData?.role === 'model'
                        ) {
                            addToast('error', 'Your account is restricted')
                            return
                        }

                        postImgRef.current?.click()
                    }}
                    desc={t('addImage')}
                    customClass="newpost__input__buttons__button"
                    type="medium"
                    input={
                        <input
                            ref={postImgRef}
                            type="file"
                            onChange={handleSetUpload}
                            multiple
                            id="post__img__upload"
                            accept={acceptedMediaFormats}
                            hidden
                            /* key={Math.random().toString(36)} */
                            disabled={isStructuredPost}
                        />
                    }
                />

                <IconButton
                    disabled={payoutDisabled}
                    customClass="newpost__input__buttons__button"
                    icon={
                        <img
                            src={
                                activeChatIcon
                                    ? Icons.chat_imgb_active
                                    : Icons.chat_imgb
                            }
                            style={{
                                width: 20,
                                height: 20,
                                filter: activeChatIcon
                                    ? 'none'
                                    : 'brightness(0) saturate(100%)'
                            }}
                            alt="chat icon"
                        />
                    }
                    clickFn={() => {
                        setActiveChatIcon(true)

                        if (userData?.status === 'restricted') {
                            addToast('error', t('messageReactRestricted'))
                            return
                        }

                        modalData.addModal(
                            'Shared Media',
                            <SharedMediaModal
                                submitFn={(val: any[]) => {
                                    const media = val.flatMap(normalizeSharedMediaItem)

                                    if (!media.length) {
                                        addToast('error', 'No reusable media found')
                                        return
                                    }

                                    void appendAttachments(media)
                                }}
                                checkingData="checkingData"
                                defaultType="feed"
                            />,
                            true,
                            true,
                            'modal--unshared-zlow'
                        )
                    }}
                    desc="Shared Media"
                    type="medium"
                />

                <IconButton
                    disabled={payoutDisabled}
                    icon={
                        <SvgIcons.IconVideoOutline
                            color={
                                postType
                                    ? postType === 'media'
                                        ? '#200E32'
                                        : '#AFAFAF'
                                    : '#200E32'
                            }
                        />
                    }
                    clickFn={() => {
                        if (
                            userData?.status === 'restricted' &&
                            userData?.role === 'model'
                        ) {
                            addToast('error', 'Your account is restricted')
                            return
                        }

                        if (!canKeepUsingMediaButtons) return

                        handleOpenRecorder('video')
                    }}
                    desc={t('addVideo')}
                    customClass="newpost__input__buttons__button"
                    type="medium"
                />

                <IconButton
                    disabled={payoutDisabled}
                    icon={
                        <SvgIcons.IconPoll
                            color={
                                isStructuredPost
                                    ? postType === 'poll'
                                        ? '#200E32'
                                        : '#AFAFAF'
                                    : canOpenPollOrGoal
                                    ? '#200E32'
                                    : '#AFAFAF'
                            }
                        />
                    }
                    clickFn={() => {
                        if (!canOpenPollOrGoal) return
                        handleAddPollOpen()
                    }}
                    desc={t('addPoll')}
                    customClass="newpost__input__buttons__button"
                    type="medium"
                />

                <IconButton
                    disabled={payoutDisabled}
                    icon={
                        <SvgIcons.IconGoal
                            color={
                                isStructuredPost
                                    ? postType === 'goal'
                                        ? '#200E32'
                                        : '#AFAFAF'
                                    : canOpenPollOrGoal
                                    ? '#200E32'
                                    : '#AFAFAF'
                            }
                        />
                    }
                    clickFn={() => {
                        if (!canOpenPollOrGoal) return
                        handleAddGoalOpen()
                    }}
                    desc={t('addPoll')}
                    customClass="newpost__input__buttons__button"
                    type="medium"
                />

                {filesAdded && (
                    <img
                        src={AllIcons.tag_user}
                        className={`newpost__tagCreatorsIcon ${
                            selectedTaggedCreators.length
                                ? 'newpost__tagCreatorsIcon--active'
                                : ''
                        }`}
                        onClick={() => setTagCreatorsOpen(prev => !prev)}
                        alt="Tag creators"
                    />
                )}
            </div>

            {filesAdded && (
                <div className="newpost__tagCreatorsWrap" ref={tagCreatorsRef}>
                    {tagCreatorsOpen && (
                        <div className="newpost__tagCreatorsModal">
                            <div className="newpost__tagCreatorsModal__list">
                                {!creatorSearchTerm.trim() &&
                                    selectedTaggedCreators.map(creator => (
                                        <div
                                            key={creator.id}
                                            className="newpost__tagCreatorsModal__item newpost__tagCreatorsModal__item--selected"
                                        >
                                            <img
                                                src={creator.avatarUrl}
                                                alt={creator.display}
                                            />

                                            <div>
                                                <strong>{creator.name}</strong>
                                                <span>@{creator.display}</span>
                                            </div>

                                            <button
                                                type="button"
                                                className="newpost__tagCreatorsModal__remove"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    removeTaggedCreator(creator.id)
                                                }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}

                                {creatorSearchLoading && (
                                    <div className="newpost__tagCreatorsModal__loader">
                                        <BeatLoader size={8} />
                                    </div>
                                )}

                                {!creatorSearchLoading &&
                                    creatorSearchTerm.trim() &&
                                    creatorResults.length === 0 && (
                                        <div className="newpost__tagCreatorsModal__empty">
                                            No Creators Found
                                        </div>
                                    )}

                                {!creatorSearchLoading &&
                                    creatorResults
                                        .filter(
                                            creator =>
                                                !selectedTaggedCreators.some(
                                                    selected =>
                                                        selected.id === creator.id
                                                )
                                        )
                                        .map(creator => {
                                            const isPendingShowOnPost =
                                                pendingShowOnPostCreator?.id ===
                                                creator.id

                                            return (
                                                <button
                                                    key={creator.id}
                                                    type="button"
                                                    className="newpost__tagCreatorsModal__item"
                                                    onClick={() => {
                                                        if (!isPendingShowOnPost) {
                                                            setPendingShowOnPostCreator(
                                                                creator
                                                            )
                                                        }
                                                    }}
                                                >
                                                    {!isPendingShowOnPost ? (
                                                        <>
                                                            <img
                                                                src={creator.avatarUrl}
                                                                alt={creator.display}
                                                            />

                                                            <div>
                                                                <strong>
                                                                    {creator.name}
                                                                </strong>
                                                                <span>
                                                                    @{creator.display}
                                                                </span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="newpost__tagCreatorsModal__confirm">
                                                            <span>Public Tag?</span>

                                                            <div className="newpost__tagCreatorsModal__confirmActions">
                                                                <button
                                                                    type="button"
                                                                    onClick={e => {
                                                                        e.stopPropagation()
                                                                        addTaggedCreator(
                                                                            creator,
                                                                            false
                                                                        )
                                                                    }}
                                                                >
                                                                    No
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    onClick={e => {
                                                                        e.stopPropagation()
                                                                        addTaggedCreator(
                                                                            creator,
                                                                            true
                                                                        )
                                                                    }}
                                                                >
                                                                    Yes
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </button>
                                            )
                                        })}
                            </div>

                            <input
                                ref={tagSearchRef}
                                className="newpost__tagCreatorsModal__search"
                                placeholder="Tag Creators"
                                value={creatorSearchTerm}
                                autoFocus
                                onChange={e => {
                                    const value = e.target.value

                                    setCreatorSearchTerm(value)

                                    if (!value.trim()) {
                                        setCreatorSearchLoading(false)
                                        setCreatorResults([])
                                        return
                                    }

                                    setCreatorSearchLoading(true)
                                    debouncedCreatorSearch(value)
                                }}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}